import { useState } from 'react'
import { useChecklistTemplates, useApplyChecklistTemplate } from '../hooks/useChecklists'
import { ModalShell, CancelButton, PrimaryButton, ServerError } from './modal-form'

// ChecklistTemplatePickerModal - shown when the user clicks "Apply a template"
// on the Checklists tab. Lists active templates with their item count. On
// apply, the server clones the template's items into per-event rows (with
// computed due_dates based on event.start_date - days_before_event).

interface Props {
  eventId: number
  onClose: () => void
}

export function ChecklistTemplatePickerModal({ eventId, onClose }: Props) {
  const { data: templates, isLoading, error } = useChecklistTemplates()
  const apply = useApplyChecklistTemplate(eventId)
  const [selected, setSelected] = useState<number | null>(null)

  function handleSubmit() {
    if (selected == null) return
    apply.mutate(selected, { onSuccess: () => onClose() })
  }

  const body = (
    <div className="space-y-3">
      {isLoading && <p className="text-sm text-ink-soft font-mono">Loading templates…</p>}

      {error && (
        <ServerError title="Could not load templates" error={error as Error} />
      )}

      {templates && templates.length === 0 && (
        <p className="text-sm italic text-ink-soft">
          No templates yet. An admin can create one from{' '}
          <span className="font-mono">Admin → Checklist Templates</span>.
        </p>
      )}

      {templates && templates.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {templates.map((t) => (
            <label
              key={t.id}
              className={
                'flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ' +
                (selected === t.id
                  ? 'border-green bg-green-soft/40'
                  : 'border-line bg-white hover:bg-parchment')
              }
            >
              <input
                type="radio"
                name="template"
                value={t.id}
                checked={selected === t.id}
                onChange={() => setSelected(t.id)}
                className="mt-1 accent-green"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{t.name}</div>
                {t.description && (
                  <div className="text-xs text-ink-soft mt-0.5">{t.description}</div>
                )}
                <div className="text-xs text-ink-soft font-mono mt-0.5">
                  {t.itemCount} item{t.itemCount === 1 ? '' : 's'}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      {apply.error && (
        <ServerError title="Could not apply template" error={apply.error as Error} />
      )}
    </div>
  )

  const footer = (
    <>
      <CancelButton onClick={onClose} disabled={apply.isPending} />
      <PrimaryButton
        onClick={handleSubmit}
        disabled={selected == null || apply.isPending}
        type="button"
      >
        {apply.isPending ? 'Applying…' : 'Apply template'}
      </PrimaryButton>
    </>
  )

  return (
    <ModalShell
      title="Apply a template"
      onClose={onClose}
      body={body}
      footer={footer}
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}
    />
  )
}
